// generatePreQualpdf.js
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

const fetchImageBytes = async (url) => {
  const res = await fetch(url);
  return await res.arrayBuffer();
};

function formatCurrency(value) {
  const num = Number(value);
  if (isNaN(num)) return "$0.00";
  return (
    "$" +
    num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

async function generatePreQualPDF(result, formData) {
  const letters = [];

  const fullName = `${formData.firstName || ""} ${formData.lastName || ""}`.trim();
  const spouseName =
    formData.spouseFirstName &&
    formData.spouseLastName &&
    formData.prenuptialAgreement !== "Yes"
      ? `${formData.spouseFirstName} ${formData.spouseLastName}`
      : "";
  
  const combinedName = fullName && spouseName
    ? `${fullName} and ${spouseName}`
    : fullName || spouseName || "Client";
  

  const loanTypes = result.eligibleProducts;
  const hasGrant = result.grantEligible;
  const grantLine =
    "This prequalification is contingent upon the approval of the AFV HBA CDBG-DR $45,000 grant.";
  const today = new Date().toLocaleDateString();

  for (const loanType of loanTypes) {
    const normalizedType = loanType.toUpperCase();
    const maxPrice = result.pricing[normalizedType] ?? 0;
    const grantPrice =
      hasGrant && result.grantPricing
        ? result.grantPricing[normalizedType]
        : null;

    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([612, 792]);
    const timesFont = await pdfDoc.embedFont("Times-Roman");
    const boldFont = await pdfDoc.embedFont("Times-Bold");

    // ... rest of your existing drawing logic

    const drawText = (text, x, y, size = 12, bold = false) => {
      page.drawText(text, {
        x,
        y,
        size,
        font: bold ? boldFont : timesFont,
        color: rgb(0, 0, 0),
      });
    };

    const hmblBytes = await fetchImageBytes("/assets/hmbl.png");
    const ehopBytes = await fetchImageBytes("/assets/EHOP.png");
    const sigBlockBytes = await fetchImageBytes("/assets/ECF.png");

    const hmblImage = await pdfDoc.embedPng(hmblBytes);
    const ehopImage = await pdfDoc.embedPng(ehopBytes);
    const sigBlock = await pdfDoc.embedPng(sigBlockBytes);

    // HMBL logo top center
    page.drawImage(hmblImage, {
      x: 160,
      y: 680,
      width: 300,
      height: 120,
    });

    drawText(today, 50, 670, 12, true);

    drawText("CLIENT:", 50, 620, 12, true);
    drawText(combinedName, 140, 620);

    drawText("LOAN:", 50, 560, 12, true);
    drawText(loanType, 140, 560);

    drawText("PROPERTY:", 50, 498, 12, true);

    const street = (formData.propertyStreet || "--TO BE DETERMINED--")
      .trim()
      .toUpperCase();
    const county = formData.location?.trim().toUpperCase() || "SAN JUAN";
    const zip = formData.propertyZip?.trim() || "00917";

    drawText(street, 140, 498);
    drawText(`${county}, PUERTO RICO, ${zip}`, 140, 483);
    drawText("CASE NUMBER: N/A", 140, 468);

    let y = 400;
    const lineSpacing = 18;
    const isMarried =
      formData.maritalStatus === "Married" &&
      formData.prenuptialAgreement !== "Yes";
    const qualificationVerb = isMarried ? "have" : "has";

    const paragraph = (
      `Based on the income and credit history information provided by the client(s) at our initial interview, ` +
      `${combinedName} ${qualificationVerb} been prequalified for a purchase contract with a selling price of ` +
      `${formatCurrency(
        grantPrice && grantPrice > 0 ? grantPrice : maxPrice
      )}, financed through a(n) ${loanType} mortgage loan.` +
      (hasGrant ? ` ${grantLine}` : "")
    ).replace(/\n/g, " "); // strip newlines

    const wrapText = (text, maxWidth, font, size) => {
      const words = text.split(" ");
      const lines = [];
      let line = "";

      for (let word of words) {
        const testLine = line.length ? `${line} ${word}` : word;
        const width = font.widthOfTextAtSize(testLine, size);
        if (width < maxWidth) {
          line = testLine;
        } else {
          lines.push(line);
          line = word;
        }
      }
      if (line) lines.push(line);
      return lines;
    };

    const paragraphLines = wrapText(paragraph, 500, timesFont, 12);
    paragraphLines.forEach((line) => {
      drawText(line, 50, y, 12);
      y -= lineSpacing;
    });

    // Keep your additional bullet points after the main paragraph
    const extraLines = [
      "",
      "The mortgage loan and pre-qualification are subject to compliance with the required documents and",
      "credit history of the financial instrument to be finally used at closing. This is not a final approval.",
      "",
      "This prequalification does not intend or purport to confer any rights or privileges upon any third parties",
      "including, but not limited to, sellers of real property, real estate brokers, or their agents.",
      "",
      "For any questions regarding this pre-qualification letter please contact us at 787-983-1103.",
      "",
      "Sincerely,",
    ];

    extraLines.forEach((line) => {
      const isBold = line.includes("787-983-1103") || line === "Sincerely,";
      drawText(line, 50, y, 12, isBold);
      y -= lineSpacing;
    });

    page.drawImage(sigBlock, {
      x: 50,
      y: 45,
      width: 220,
      height: 120,
    });

    page.drawImage(ehopImage, {
      x: 510,
      y: 60,
      width: 60,
      height: 60,
    });

    // footer
    const footerText =
      "Marginal Santa Rosa Bloq. 51 #55 Local #2, Bayamón PR 00956 Tel. 787-785-6500";
    const footerWidth = timesFont.widthOfTextAtSize(footerText, 9);
    const footerX = (612 - footerWidth) / 2;
    drawText(footerText, footerX, 20, 9);

    const pdfBytes = await pdfDoc.save();
    letters.push({ loanType, pdfBytes });
  }

  return letters;
}

export default generatePreQualPDF;
